import { Box, Pill, Text } from '@fullstory/components';
import { PillProps } from '@fullstory/components/legacy/Pill';
import { NativePlatform } from '@fullstory/proto/fs/api/segment';
import { SelectorChain } from '@fullstory/proto/fs/selectors';
import {
  ObjectInfo,
  ObjectPathPattern,
  RendererDimensions,
  RendererPointerDownEvent,
  RendererPointerMoveEvent,
} from '@fullstory/renderer-shared';
import { emptyRect, outsetBy } from '@fullstory/util/geometry';
import { VirtualElement } from '@popperjs/core/lib/types';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import { RendererClient } from '../internal';
import {
  GroupedObjectInfoWithLabel,
  ObjectCollectionHighlight,
  ObjectCollectionWithLabels,
  RectHighlight,
} from './ObjectHighlight';
import { fillParentStyles, selectionOrder, toNativePlatform } from './util';

const pillVariantByType: { [key: string]: PillProps['variant'] } = {
  'existing-context': 'primary',
  'suggestion-context': 'intelligence',
};

const pillIconByType: { [key: string]: PillProps['iconLeft'] } = {
  'suggestion-context': 'ai',
};

export type ElementInspectorOverlayProps = {
  highlightedObjects: ObjectCollectionWithLabels;
  constrainInspected?: boolean;
  onInspectSelector?: (
    rawSelector: SelectorChain,
    nativePlatform: NativePlatform | undefined,
  ) => void;
  onClickExisting?: (object: GroupedObjectInfoWithLabel) => void;
  rendererClient: RendererClient;
};

export const ElementInspectorOverlay: React.FC<ElementInspectorOverlayProps> = memo(
  function ElementInspectorOverlay({
    highlightedObjects,
    constrainInspected,
    onInspectSelector,
    onClickExisting,
    rendererClient,
  }) {
    // Make the renderer interactive to allow element inspection and scrolling.
    useEffect(() => {
      rendererClient.setMode({
        mode: 'interactive',
        cursor: onInspectSelector ? 'crosshair' : 'default',
      });
    }, [rendererClient, onInspectSelector]);

    const highlightedObjectsRef = useRef(highlightedObjects);
    useEffect(() => {
      highlightedObjectsRef.current = highlightedObjects;
    }, [highlightedObjects]);

    const filterInspectedObjects = useCallback(
      async (objects: ObjectInfo[]) => {
        if (!constrainInspected) {
          return objects;
        }
        // need to check constraints before returning results
        // we're going to go the cheap route and just query for paths that we know about
        const patterns: ObjectPathPattern[] = objects.map(obj => ({
          type: 'path',
          path: obj.path,
          groups: ['obj'],
        }));
        for (const [path, obj] of Object.entries(highlightedObjectsRef.current)) {
          if (obj.groups.includes('inspected')) {
            patterns.push({ type: 'path', path, groups: ['inspected'] });
          }
        }
        const collection = await rendererClient.queryObjects({
          patterns,
          computeDescendants: true,
        });
        // now we just need to find at least one result where an 'inspected' object contains an inspected object path in its descendants
        const constrainedObjectPaths = new Set<string>();
        Object.values(collection).forEach(obj => {
          if (obj.groups.includes('inspected')) {
            obj.descendants?.forEach(descendant => {
              if (collection[descendant.path]?.groups.includes('obj')) {
                constrainedObjectPaths.add(descendant.path);
              }
            });
          }
        });
        return objects.filter(obj => constrainedObjectPaths.has(obj.path));
      },
      [constrainInspected, rendererClient],
    );

    // The rect associated with the element the user is hovering over, if any.
    const [hoveredRect, setHoveredRect] = useState<DOMRect | undefined>(undefined);

    // The user can click repeatedly without moving their mouse to "expand" their
    // selection. This ref holds the stack of selectors that the user is
    // clicking through. The first element is the most recently clicked
    // selector; each click rotates the stack. Note that these selectors aren't
    // the ones we're highlighting; we highlight the optimized version.
    const clickedSelectors = useRef<SelectorChain[]>([]);

    // When the user clicks, recompute the inspected selector based on the element
    // they clicked on.
    const handleClick = useCallback(
      async (e: RendererPointerDownEvent) => {
        const targets = await filterInspectedObjects(e.targets);

        if (clickedSelectors.current.length === 0) {
          // There's no existing stack of clicked selectors; create one.
          clickedSelectors.current = targets.sort(selectionOrder).map(t => t.selector);
        } else {
          // Rotate the stack of clicked selectors.
          clickedSelectors.current = [
            ...clickedSelectors.current.slice(1),
            ...clickedSelectors.current.slice(0, 1),
          ];
        }
        const nextSelector = clickedSelectors.current[0];
        if (!nextSelector) {
          return;
        }

        // Clear the hovered rect and stop any pending move, since it would
        // otherwise reset the "click to expand" process.
        setHoveredRect(undefined);

        // And determine the native platform from the target data -- will just take the topmost target for now.
        const nativePlatform = toNativePlatform(targets[0]?.platform);

        onInspectSelector?.(nextSelector, nativePlatform);
      },
      [filterInspectedObjects, onInspectSelector],
    );
    useEffect(
      () => rendererClient.addEventListener('pointerdown', handleClick),
      [handleClick, rendererClient],
    );

    // When the user moves the mouse within the overlay, update the hovered rect.
    const handleMove = useMemo(
      () => async (e: RendererPointerMoveEvent) => {
        const hoveredTarget = e.targets.sort(selectionOrder)[0];

        const onClear = () => {
          setHoveredRect(undefined);
        };
        if (!hoveredTarget) {
          onClear();
          return;
        }
        const onSuccess = () => {
          setHoveredRect(hoveredTarget.rect);
          clickedSelectors.current = [];
        };
        // only a single target being tested here, if any still remain after filtering we can trust it was the one passed in
        const hoveredTargets = await filterInspectedObjects([hoveredTarget]);
        if (hoveredTargets.length) {
          onSuccess();
        } else {
          onClear();
        }
      },
      [filterInspectedObjects],
    );
    useEffect(
      () => rendererClient.addEventListener('pointermove', handleMove),
      [handleMove, rendererClient],
    );

    // Reset all transient interactions, like the "click to expand" and hover
    // behaviors, when the user moves the mouse out of the overlay.
    const cancelInteractions = useCallback(() => {
      clickedSelectors.current = [];
      setHoveredRect(undefined);
    }, []);
    useEffect(
      () => rendererClient.addEventListener('pointerout', cancelInteractions),
      [cancelInteractions, rendererClient],
    );

    // Likewise, reset transient interactions when the user starts scrolling/resizing.
    const [scrolling, setScrolling] = useState(false);
    const [resizing, setResizing] = useState(false);
    const resizingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const contentChanging = scrolling || resizing;

    const handleScrollStart = useCallback(() => {
      setScrolling(true);
      cancelInteractions();
    }, [cancelInteractions]);
    useEffect(
      () => rendererClient.addEventListener('scrollstart', handleScrollStart),
      [handleScrollStart, rendererClient],
    );
    const handleScrollEnd = useCallback(() => {
      setScrolling(false);
      cancelInteractions();
    }, [cancelInteractions]);
    useEffect(
      () => rendererClient.addEventListener('scrollend', handleScrollEnd),
      [handleScrollEnd, rendererClient],
    );

    // Track the dimensions of the renderer.
    const [dimensions, setDimensions] = useState<RendererDimensions>({
      contentArea: emptyRect(),
      rendererArea: emptyRect(),
    });
    useEffect(
      () =>
        rendererClient.addResizeObserver(e => {
          setDimensions(e.dimensions);
          setResizing(true);
          if (resizingTimeout.current) {
            clearTimeout(resizingTimeout.current);
          }
          resizingTimeout.current = setTimeout(() => {
            setResizing(false);
            resizingTimeout.current = null;
          }, 250);
          cancelInteractions();
        }),
      [cancelInteractions, rendererClient],
    );
    useEffect(
      () => () => {
        if (resizingTimeout.current) {
          clearTimeout(resizingTimeout.current);
          resizingTimeout.current = null;
        }
        setResizing(false);
      },
      [],
    );

    // This helper constructs a mask which fades out overlay content at the
    // edges of the content area. This is a bit more visually appealing than a
    // hard stop.
    const mask = useMemo(() => {
      const fadeOutRect = outsetBy(dimensions.contentArea, 15);
      const rect = dimensions.contentArea;
      const maskLayers = [
        `linear-gradient(to right, transparent, transparent ${fadeOutRect.left}px, white ${rect.left}px, white ${rect.right}px, transparent ${fadeOutRect.right}px, transparent)`,
        `linear-gradient(to bottom, transparent, transparent ${fadeOutRect.top}px, white ${rect.top}px, white ${rect.bottom}px, transparent ${fadeOutRect.bottom}px, transparent)`,
      ];
      return maskLayers.join(' intersect, ');
    }, [dimensions.contentArea]);

    const [virtualElement, setVirtualElement] = useState<VirtualElement>({
      getBoundingClientRect: () => new DOMRect(0, 0, 0, 0),
    });

    const [pillLabel, setPillLabel] = useState<{
      path: string;
      label: string;
      type: string | undefined;
    }>();
    const [pillLabelElement, setPillLabelElement] = useState<HTMLDivElement | null>(null);
    const { styles, attributes } = usePopper(virtualElement, pillLabelElement, {
      placement: 'top',
    });

    const pointerEvents = useMemo(() => {
      if (!onClickExisting) {
        return undefined;
      }
      return {
        onClick: onClickExisting,
        onMouseEnter: (object: GroupedObjectInfoWithLabel, type: string | undefined) => {
          if (object.label) {
            setPillLabel({ path: object.path, label: object.label, type });
          }
        },
        onMouseMove: (event: React.MouseEvent) => {
          setVirtualElement({
            getBoundingClientRect: () => new DOMRect(event.clientX, event.clientY, 0, 0),
          });
        },
        onMouseLeave: (object: GroupedObjectInfoWithLabel, type: string | undefined) => {
          setPillLabel(existing =>
            existing?.path === object.path && existing?.type === type ? undefined : existing,
          );
        },
      };
    }, [onClickExisting]);

    return (
      <>
        <div
          data-testid="element-inspector-overlay"
          role="none"
          tabIndex={-1}
          style={{
            ...fillParentStyles,
            position: 'absolute',
            top: 0,
            left: 0,
            pointerEvents: 'none',
            opacity: contentChanging ? 0 : 1,
            transition: contentChanging ? 'unset' : 'opacity 0.2s ease-in-out',
            mask,
          }}
        >
          <ObjectCollectionHighlight
            group="inspected-context"
            objects={highlightedObjects}
            type="inspected-context"
          />
          <ObjectCollectionHighlight
            group="inspected"
            objects={highlightedObjects}
            type="inspected"
          />
          <ObjectCollectionHighlight
            group="existing-context"
            objects={highlightedObjects}
            type="existing-context"
            pointerEvents={pointerEvents}
          />
          <ObjectCollectionHighlight
            group="suggestion-context"
            objects={highlightedObjects}
            type="suggestion-context"
            pointerEvents={pointerEvents}
          />
          {hoveredRect && onInspectSelector && (
            <RectHighlight key="hovered" rect={hoveredRect} type="hovered" />
          )}
        </div>
        {pillLabel && (
          <Box
            ref={setPillLabelElement}
            style={{
              ...styles.popper,
              ...attributes.popper,
              pointerEvents: 'none',
            }}
          >
            <Pill
              variant={pillVariantByType[pillLabel.type ?? ''] ?? 'primary'}
              iconLeft={pillIconByType[pillLabel.type ?? ''] ?? undefined}
              opaque
            >
              <Text appearance="bodyMedium" noWrap tag="span">
                {pillLabel.label}
              </Text>
            </Pill>
          </Box>
        )}
      </>
    );
  },
);
